<!-- PasswordPrompt.vue -->
<template>
    <Teleport to="body">
        <div class="password-prompt">
            <NavExit @click="goBack" class="close-button" />

            <div class="prompt-content">
                <h3>{{ marketDisplayName }}</h3>
                <p>This content is password protected.</p>

                <template v-if="!misconfigured">
                    <form class="password-form" @submit.prevent="submit">
                        <input
                            type="password"
                            v-model="password"
                            placeholder="Enter password"
                            class="password-input"
                            :disabled="submitting"
                            autofocus
                        />
                        <button type="submit" class="unlock-button" :disabled="submitting || !password">
                            Unlock
                        </button>
                    </form>
                    <p v-if="error" class="error-message">{{ error }}</p>
                </template>
                <template v-else>
                    <p class="error-message">This content is currently unavailable.</p>
                </template>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePortfolioStore } from '@/stores/portfolio';
import { useSpaceScale, useTextScale, useSemanticColors } from '@/composables/useStyleVariables';
import NavExit from './interactive/NavExit.vue';

const props = defineProps({
    marketKey: {
        type: String,
        required: true
    }
});

const route = useRoute();
const router = useRouter();
const portfolioStore = usePortfolioStore();
const { space } = useSpaceScale();
const { fontSize, lineHeight } = useTextScale();
const { bgColors, textColors, buttonColors } = useSemanticColors();

const password = ref('');
const error = ref(null);
const submitting = ref(false);

const marketConfig = computed(() => portfolioStore.portfolioJson?.markets?.[props.marketKey]);
const marketDisplayName = computed(() => marketConfig.value?.displayName ?? props.marketKey);
const misconfigured = computed(() => !marketConfig.value?.passwordHash || !marketConfig.value?.passwordSalt);

onMounted(() => {
    if (misconfigured.value) {
        console.warn(`Market "${props.marketKey}" has locked content but no password configured (passwordHash/passwordSalt is null).`);
    }
});

const submit = async () => {
    error.value = null;
    submitting.value = true;
    try {
        const ok = await portfolioStore.unlockMarket(props.marketKey, password.value);
        if (!ok) error.value = 'Incorrect password. Please try again.';
    } finally {
        submitting.value = false;
        password.value = '';
    }
};

const goBack = () => {
    const defaultSection = portfolioStore.portfolioJson?.markets?.[props.marketKey]?.sections?.[0];

    if (route.params.project) {
        router.push(`/${props.marketKey}/${route.params.section}`);
    } else if (defaultSection) {
        router.push(`/${props.marketKey}/${defaultSection}`);
    } else {
        router.push(`/${props.marketKey}`);
    }
};
</script>

<style scoped>
.password-prompt {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100vh;
    background: v-bind('bgColors.light');
    color: v-bind('textColors.bgLight');
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
}

.close-button {
    position: fixed;
    top: v-bind('space.sp3 + "px"');
    right: v-bind('space.sp5 + "px"');
    width: v-bind('space.sp7 + "px"');
    height: auto;
    cursor: pointer;
    z-index: 14000;
}

.prompt-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: v-bind('space.sp4 + "px"');
    text-align: center;
    padding: v-bind('space.sp7 + "px"');
    max-width: 480px;
}

.prompt-content h3 {
    font-size: v-bind('fontSize.h5 + "px"');
    line-height: v-bind('lineHeight.h5 + "px"');
    font-weight: 500;
}

.prompt-content p {
    font-size: v-bind('fontSize.body + "px"');
    line-height: v-bind('lineHeight.body + "px"');
}

.password-form {
    display: flex;
    gap: v-bind('space.sp3 + "px"');
    margin-top: v-bind('space.sp3 + "px"');
}

.password-input {
    font-size: v-bind('fontSize.body + "px"');
    padding: v-bind('space.sp3 + "px"') v-bind('space.sp4 + "px"');
    border: 1px solid v-bind('buttonColors.neutralDark');
    background: v-bind('bgColors.light');
    color: v-bind('textColors.bgLight');
}

.password-input:focus {
    outline: none;
    border-color: v-bind('buttonColors.accent');
}

.unlock-button {
    font-size: v-bind('fontSize.body + "px"');
    padding: v-bind('space.sp3 + "px"') v-bind('space.sp5 + "px"');
    background: v-bind('buttonColors.accent');
    color: v-bind('buttonColors.neutralLight');
    border: none;
    cursor: pointer;
}

.unlock-button:hover:not(:disabled) {
    background: v-bind('buttonColors.hover');
}

.unlock-button:disabled {
    opacity: 0.5;
    cursor: default;
}

.error-message {
    color: v-bind('buttonColors.accent');
    font-size: v-bind('fontSize.small + "px"');
}
</style>
